import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Globe, X } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";

/**
 * Soft, non-intrusive language suggestion banner.
 *
 * Shows once per session when:
 *   - The user has *not* yet explicitly chosen a language
 *     (no `fg_locale_user_chose` flag set)
 *   - The browser's preferred language differs from the active locale
 *   - The detected browser locale is one we actually support
 *
 * Critically: it never auto-switches. The user is always in control.
 *
 * Hidden on /admin to avoid stacking with admin save bars + composer
 * modals. Admins explicitly pick locales from inside the panel anyway.
 */
const DISMISS_KEY = "fg_locale_banner_dismissed_v1";
const CHOSE_KEY = "fg_locale_user_chose";
const HIDE_ON_PATH_PREFIXES = ["/admin"];

function detectBrowserLocale() {
  if (typeof navigator === "undefined") return null;
  const langs = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || navigator.userLanguage];
  return (langs[0] || "").toLowerCase().split("-")[0];
}

export default function LanguageSuggestionBanner() {
  const { locale, setLocale, supported, t } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHiddenRoute = HIDE_ON_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));

  const suggestion = useMemo(() => {
    if (typeof window === "undefined") return null;
    if (onHiddenRoute) return null;
    try {
      if (sessionStorage.getItem(DISMISS_KEY)) return null;
      if (localStorage.getItem(CHOSE_KEY)) return null;
    } catch { /* ignore */ }
    const detected = detectBrowserLocale();
    if (!detected || detected === locale) return null;
    const match = (supported || []).find((s) => s.code === detected);
    if (!match) return null;
    return match;
  }, [locale, supported, onHiddenRoute]);

  useEffect(() => {
    if (suggestion) {
      // Slight delay so it doesn't crowd the page load
      const tm = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(tm);
    }
    setOpen(false);
    return undefined;
  }, [suggestion]);

  if (!suggestion || !open) return null;

  const lang = suggestion.native || suggestion.name;

  const accept = () => {
    setLocale(suggestion.code);
    try {
      localStorage.setItem(CHOSE_KEY, "1");
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch { /* ignore */ }
    setOpen(false);
  };
  const decline = () => {
    try {
      localStorage.setItem(CHOSE_KEY, "1");
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch { /* ignore */ }
    setOpen(false);
  };
  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  return (
    <div
      role="region"
      aria-live="polite"
      aria-labelledby="language-suggestion-title"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[60] rounded-2xl border border-emerald-100 bg-white/95 backdrop-blur-xl shadow-xl p-4 flex gap-3 items-start animate-in fade-in slide-in-from-bottom-4 duration-300"
      data-testid="language-suggestion-banner"
    >
      <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-50 text-emerald-700" aria-hidden="true">
        <Globe className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p id="language-suggestion-title" className="font-display text-base text-emerald-950 leading-snug" data-testid="language-suggestion-title">
          {t("banner.suggest_title", { language: lang })}
        </p>
        <p className="text-xs text-emerald-900/65 mt-1 leading-relaxed">
          {t("banner.suggest_body")}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={accept}
            className="inline-flex items-center rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-3.5 py-1.5 transition"
            data-testid="language-suggestion-accept"
          >
            {t("banner.yes_switch", { language: lang })}
          </button>
          <button
            type="button"
            onClick={decline}
            className="inline-flex items-center rounded-full bg-white border border-emerald-200 hover:bg-emerald-50 text-emerald-800 text-xs font-semibold px-3.5 py-1.5 transition"
            data-testid="language-suggestion-decline"
          >
            {t("banner.no_keep")}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("banner.dismiss")}
        className="shrink-0 -mt-1 -mr-1 p-1.5 rounded-full text-emerald-900/55 hover:bg-emerald-50 hover:text-emerald-900 transition"
        data-testid="language-suggestion-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
